/**
 * Armes du joueur : laser, missile classique, nuée HYDRA.
 * Invariants communs (plan W05, RETEX §6.5) vérifiés par tests/weapon-invariants.test.ts :
 * - tirer sans verrou ne consomme rien et ne remet pas l'acquisition à zéro ;
 * - seuls les projectiles réellement créés consomment du stock et déclenchent la cadence ;
 * - les projectiles ne touchent ni leur lanceur ni les autres projectiles du joueur.
 */
import type { ShipProfile } from '../../data';
import { Vec3, segmentSphere } from '../core/math';
import type { PlayerWeaponsState, Projectile, Ship } from '../entities';
import type { PlayerInput } from '../pilots/player';
import type { SimState } from '../state';
import { findMissileTarget, lockedTarget, resetLock } from './lock';

export function createPlayerWeapons(shipId: PlayerWeaponsState['shipId'], profile: ShipProfile): PlayerWeaponsState {
  return {
    shipId,
    missiles: profile.missiles.initial,
    maxMissiles: profile.missiles.max,
    swarm: profile.swarm.initial,
    maxSwarm: profile.swarm.max,
    laserCooldown: 0,
    lastMissileTime: -Infinity,
    lastSwarmTime: -Infinity,
    lockState: 'none',
    lockTargetId: null,
    lockElapsed: 0,
    previousCandidateId: null,
    primaryWasDown: false,
    secondaryWasDown: false,
    heat: 0,
    overheated: false,
    overheatTimer: 0,
    heatCoolTimer: 0,
  };
}

export function profileOf(state: SimState): ShipProfile {
  return state.data.ships.ships[state.weapons.shipId];
}

export function updatePlayerWeapons(state: SimState, input: PlayerInput, dt: number): void {
  const w = state.weapons;
  const profile = profileOf(state);
  const primaryPressed = input.firePrimary && !w.primaryWasDown;
  const secondaryPressed = input.fireSecondary && !w.secondaryWasDown;
  w.primaryWasDown = input.firePrimary;
  w.secondaryWasDown = input.fireSecondary;
  if (!state.player.alive) return;

  // Laser : tir continu tant que le bouton est tenu, premier tir immédiat, surchauffe (phase A).
  if (profile.primary === 'laser' && profile.laser) {
    const heat = state.data.weapons.laserHeat;
    w.laserCooldown -= dt;
    let fired = false;
    if (input.firePrimary && !w.overheated) {
      while (w.laserCooldown <= 0) {
        fireLaser(state, profile);
        fired = true;
        w.laserCooldown += profile.laser.interval;
        w.heat += profile.laser.heatPerShot;
        if (w.heat >= 1) {
          w.heat = 1;
          w.overheated = true;
          w.overheatTimer = heat.overheatLockout;
          w.laserCooldown = Math.max(0, w.laserCooldown);
          state.emit({ type: 'overheat', time: state.time });
          break;
        }
      }
    } else {
      w.laserCooldown = Math.max(0, w.laserCooldown);
    }
    if (fired) {
      w.heatCoolTimer = heat.coolDelay;
    } else {
      w.heatCoolTimer = Math.max(0, w.heatCoolTimer - dt);
      if (w.heatCoolTimer <= 0) w.heat = Math.max(0, w.heat - heat.coolRate * dt);
    }
    if (w.overheated) {
      w.overheatTimer -= dt;
      if (w.overheatTimer <= 0 && w.heat <= heat.resumeAt) w.overheated = false;
    }
  }

  const missilePressed = profile.primary === 'missile' ? primaryPressed : profile.secondary === 'missile' ? secondaryPressed : false;
  if (missilePressed) fireClassicMissile(state, profile);
  if (profile.secondary === 'swarm' && secondaryPressed) fireSwarm(state, profile);
}

/** Un tir de laser : un faisceau, ou une gerbe (`pellets`) au centre puis en couronne de rayon `spreadDeg`. */
export function fireLaser(state: SimState, profile: ShipProfile): void {
  const laser = profile.laser;
  if (!laser) return;
  const player = state.player;
  const forward = player.forward();
  const right = player.right();
  const up = player.up();
  const start = player.localToWorld(profile.hardpoints.laserOrigin);
  const pellets = laser.pellets ?? 1;
  const spread = Math.tan(((laser.spreadDeg ?? 0) * Math.PI) / 180);
  const guns = profile.hardpoints.guns.map((g) => player.localToWorld(g).toArray());
  for (let i = 0; i < pellets; i++) {
    let direction = forward;
    if (i > 0) {
      const angle = (2 * Math.PI * (i - 1)) / (pellets - 1);
      direction = forward.add(right.scale(Math.cos(angle) * spread)).add(up.scale(Math.sin(angle) * spread)).normalized(forward);
    }
    fireBeam(state, laser, start, direction, pellets > 1 ? [guns[i % guns.length]!] : guns);
  }
}

function fireBeam(state: SimState, laser: NonNullable<ShipProfile['laser']>, start: Vec3, forward: Vec3, muzzles: [number, number, number][]): void {
  const player = state.player;
  const hit = traceLaser(state, start, forward, laser.range);
  const end = hit ? hit.point : start.add(forward.scale(laser.range));
  state.emit({
    type: 'laserFired',
    time: state.time,
    shipId: player.id,
    muzzles,
    end: end.toArray(),
    hit: hit !== null,
    color: laser.beamColor,
    width: laser.beamWidth,
    duration: Math.min(laser.interval, 0.12),
  });
  if (!hit) return;
  state.telemetry.recordLaserHit(hit.ship !== null);
  const heavy = laser.impact === 'heavy';
  state.emit({
    type: 'impact',
    time: state.time,
    position: hit.point.toArray(),
    normal: hit.normal.toArray(),
    surface: hit.ship ? (hit.ship.kind === 'boss' ? 'boss' : hit.ship.kind === 'heavy' ? 'heavy' : 'enemy') : 'rock',
    heavy,
  });
  if (hit.ship) {
    state.applyDamage(hit.ship, laser.damage, {
      sourceShip: player,
      source: 'player',
      weapon: 'laser',
      position: hit.point,
      origin: player.position,
    });
  }
}

export interface LaserHit {
  point: Vec3;
  normal: Vec3;
  ship: Ship | null;
}

/** Premier obstacle ou ennemi sur l'axe. */
export function traceLaser(state: SimState, start: Vec3, direction: Vec3, range: number): LaserHit | null {
  const end = start.add(direction.scale(range));
  let bestT = Infinity;
  let best: LaserHit | null = null;
  const rock = state.collision.raycast(start, direction, range);
  if (rock) {
    bestT = rock.distance / range;
    best = { point: rock.point, normal: rock.normal, ship: null };
  }
  for (const ship of state.ships) {
    if (!ship.alive || ship.team !== 'enemy') continue;
    const t = segmentSphere(start, end, ship.position, ship.radius);
    if (t !== null && t < bestT) {
      bestT = t;
      const point = start.add(direction.scale(t * range));
      best = { point, normal: point.sub(ship.position).normalized(direction.neg()), ship };
    }
  }
  return best;
}

/** Point visé par le laser (pour le réticule) : impact ou extrémité de portée. */
export function laserAimPoint(state: SimState): Vec3 | null {
  const profile = profileOf(state);
  const player = state.player;
  const range = profile.laser?.range ?? state.data.weapons.lock.range;
  const start = player.localToWorld(profile.hardpoints.laserOrigin);
  const forward = player.forward();
  const hit = traceLaser(state, start, forward, range);
  return hit ? hit.point : start.add(forward.scale(range));
}

/** Revalide la cible au départ. Sans verrou : aucun effet (ni stock, ni cadence, ni remise à zéro). */
function validatedTarget(state: SimState): Ship | null {
  const target = lockedTarget(state);
  if (!target) {
    state.emit({ type: 'missileBlocked', time: state.time, reason: 'noLock' });
    return null;
  }
  if (findMissileTarget(state)?.id !== target.id) {
    resetLock(state);
    state.emit({ type: 'missileBlocked', time: state.time, reason: 'noLock' });
    return null;
  }
  return target;
}

export function fireClassicMissile(state: SimState, profile: ShipProfile): boolean {
  const w = state.weapons;
  const cfg = state.data.weapons.missile;
  if (w.missiles <= 0) {
    state.emit({ type: 'missileBlocked', time: state.time, reason: 'empty' });
    return false;
  }
  if (state.time - w.lastMissileTime < cfg.interval) {
    state.emit({ type: 'missileBlocked', time: state.time, reason: 'cooldown' });
    return false;
  }
  const target = validatedTarget(state);
  if (!target) return false;
  const player = state.player;
  const spawn = player.localToWorld(profile.hardpoints.missile);
  const direction = target.position.sub(spawn).normalized(player.forward());
  spawnPlayerProjectile(state, {
    kind: 'missile',
    position: spawn,
    velocity: direction.scale(cfg.initialSpeed),
    radius: cfg.collisionRadius,
    damage: cfg.damage,
    blastRadius: cfg.radius,
    life: cfg.life,
    targetId: target.id,
    homingActivation: 0,
    homingActive: true,
    minSpeed: cfg.initialSpeed,
    maxSpeed: cfg.maxSpeed,
    homingAcceleration: cfg.homingAcceleration,
    color: [1, 0.22, 0.03],
  });
  w.missiles--;
  w.lastMissileTime = state.time;
  state.emit({ type: 'missileLaunched', time: state.time, kind: 'missile', count: 1, position: spawn.toArray() });
  return true;
}

export function fireSwarm(state: SimState, profile: ShipProfile): number {
  const w = state.weapons;
  const cfg = state.data.weapons.swarm;
  const burst = profile.swarm.burst;
  if (w.swarm <= 0 || burst <= 0) {
    state.emit({ type: 'missileBlocked', time: state.time, reason: 'empty' });
    return 0;
  }
  if (state.time - w.lastSwarmTime < cfg.interval) {
    state.emit({ type: 'missileBlocked', time: state.time, reason: 'cooldown' });
    return 0;
  }
  const target = validatedTarget(state);
  if (!target) return 0;
  const player = state.player;
  const active = state.projectiles.filter((p) => p.alive && p.kind === 'swarm' && p.ownerId === player.id).length;
  const count = Math.min(burst, w.swarm, Math.max(0, cfg.maxActive - active));
  if (count <= 0) {
    state.emit({ type: 'missileBlocked', time: state.time, reason: 'slots' });
    return 0;
  }
  const forward = player.forward();
  const right = player.right();
  const up = player.up();
  const base = Vec3.from(profile.hardpoints.swarmBase);
  const step = Vec3.from(profile.hardpoints.swarmPairStep);
  let spawned = 0;
  for (let index = 0; index < count; index++) {
    const side = index % 2 === 0 ? -1 : 1;
    const pair = Math.floor(index / 2);
    const local = new Vec3(side * (base.x + pair * step.x), base.y + pair * step.y, base.z + pair * step.z);
    const position = player.localToWorld(local);
    let direction: Vec3;
    let homingDelay = cfg.homingDelay;
    if (cfg.emission === 'sphere') {
      // Option « type Macross » : directions réparties sur toute la sphère, y compris vers l'arrière.
      const golden = Math.PI * (3 - Math.sqrt(5));
      const y = 1 - (2 * (index + 0.5)) / count;
      const r = Math.sqrt(Math.max(0, 1 - y * y));
      const theta = golden * index;
      direction = right.scale(Math.cos(theta) * r).add(up.scale(Math.sin(theta) * r)).add(forward.scale(y)).normalized(forward);
      homingDelay = cfg.sphereNoHomingTime;
    } else {
      const angle = (2 * Math.PI * index) / count;
      const radial = right.scale(Math.cos(angle)).add(up.scale(Math.sin(angle)));
      direction = forward.add(radial.scale(cfg.radialComponent)).normalized(forward);
    }
    spawnPlayerProjectile(state, {
      kind: 'swarm',
      position,
      velocity: direction.scale(cfg.initialSpeed).add(player.velocity.scale(cfg.emission === 'sphere' ? 1 : 0)),
      radius: cfg.collisionRadius,
      damage: cfg.damage,
      blastRadius: cfg.radius,
      life: cfg.life,
      targetId: target.id,
      homingActivation: homingDelay + index * cfg.homingStagger,
      homingActive: false,
      minSpeed: cfg.initialSpeed,
      maxSpeed: cfg.maxSpeed,
      homingAcceleration: cfg.homingAcceleration,
      color: [1, 0.18, 0.01],
    });
    spawned++;
  }
  if (spawned <= 0) return 0;
  w.swarm -= spawned;
  w.lastSwarmTime = state.time;
  state.emit({ type: 'missileLaunched', time: state.time, kind: 'swarm', count: spawned, position: player.position.toArray() });
  return spawned;
}

type ProjectileSpec = Pick<
  Projectile,
  'kind' | 'position' | 'velocity' | 'radius' | 'damage' | 'blastRadius' | 'life' | 'targetId' | 'homingActivation' | 'homingActive' | 'minSpeed' | 'maxSpeed' | 'homingAcceleration' | 'color'
>;

function spawnPlayerProjectile(state: SimState, spec: ProjectileSpec): Projectile {
  const projectile: Projectile = {
    id: state.newId(),
    team: 'player',
    ownerId: state.player.id,
    source: 'player',
    age: 0,
    alive: true,
    targetLost: false,
    offTargetSeconds: 0,
    ...spec,
    position: spec.position.clone(),
    velocity: spec.velocity.clone(),
  };
  state.projectiles.push(projectile);
  state.emit({ type: 'projectileSpawned', time: state.time, projectileId: projectile.id, kind: projectile.kind, position: projectile.position.toArray() });
  return projectile;
}
